"use client"

import { Head } from "@inertiajs/react"

import { PageHeader } from "@/components/console/page-header"
import { StatusBadge } from "@/components/ui/badge"
import { SectionHeader } from "@/components/ui/section-header"
import { SectionLabel } from "@/components/ui/section-label"
import { DashboardLayout } from "@/layouts/dashboard-layout"

interface Booking {
  token: string
  account_name: string
  program_type: string
  program_date: string
  program_start_time: string | null
  status: string
  year_level: string | null
  expected_size: number | null
}

interface Answers {
  program_start_time?: string
  expected_cohort_size?: number
  booking_manager?: string
  available_facilities?: string[]
  possible_disturbances?: string
  on_the_day_contact?: string
  critical_incident_occurred?: boolean
  critical_incidents?: string[]
  cald_backgrounds?: string[]
  wellbeing_staff?: string
  student_access_methods?: string[]
  after_hours_contact?: string
}

interface Props {
  booking: Booking
  answers: Answers
  submitted_at: string | null
  contact_names: Record<string, string>
}

const formatDate = (iso: string) =>
  new Date(iso + "T00:00:00").toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })

const Field = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
    <SectionLabel.Root size="sm" title={label} className="max-lg:hidden" />
    <div>
      <p className="text-primary text-sm lg:pt-1.5">{children}</p>
      <p className="text-tertiary text-xs lg:hidden">{label}</p>
    </div>
  </div>
)

const Divider = () => (
  <hr className="bg-border-secondary h-px w-full border-none" />
)

const formatList = (items: string[] | undefined) =>
  items?.length ? items.join(", ") : "—"

const contactName = (id: string | undefined, names: Record<string, string>) =>
  id ? (names[id] ?? "—") : "—"

export default function CheckinShow({
  booking,
  answers,
  contact_names,
}: Props) {
  return (
    <DashboardLayout>
      <Head title={`${booking.account_name} — Check-in`} />
      <div className="max-w-container mx-auto flex flex-col gap-8 px-4 lg:px-8">
        <PageHeader
          crumbs={[
            { label: "forms" },
            { label: "check-ins", href: "/dashboard" },
            { label: booking.account_name },
          ]}
          title={booking.account_name}
        >
          <div>
            <StatusBadge tier="confirmed">Submitted</StatusBadge>
          </div>
        </PageHeader>

        {/* Booking Details */}
        <section className="flex flex-col gap-5">
          <SectionHeader.Root>
            <SectionHeader.Group>
              <SectionHeader.Heading>Booking Details</SectionHeader.Heading>
            </SectionHeader.Group>
          </SectionHeader.Root>
          <div className="flex flex-col gap-5">
            <Field label="Program">{booking.program_type}</Field>
            <Divider />
            <Field label="Program Date">
              <span className="font-mono text-xs tabular-nums">
                {formatDate(booking.program_date)}
              </span>
            </Field>
            <Divider />
            <Field label="Start Time">
              <span className="font-mono text-xs tabular-nums">
                {answers.program_start_time ||
                  booking.program_start_time ||
                  "—"}
              </span>
            </Field>
            <Divider />
            <Field label="Year Level">{booking.year_level || "—"}</Field>
            <Divider />
            <Field label="Expected Attendance">
              <span className="font-mono text-xs tabular-nums">
                {answers.expected_cohort_size ?? booking.expected_size ?? "—"}
              </span>
            </Field>
            <Divider />
            <Field label="Booking Contact">
              {contactName(answers.booking_manager, contact_names)}
            </Field>
          </div>
        </section>

        {/* School Facilities */}
        <section className="flex flex-col gap-5">
          <SectionHeader.Root>
            <SectionHeader.Group>
              <SectionHeader.Heading>School Facilities</SectionHeader.Heading>
            </SectionHeader.Group>
          </SectionHeader.Root>
          <div className="flex flex-col gap-5">
            <Field label="Available Equipment">
              {formatList(answers.available_facilities)}
            </Field>
            <Divider />
            <Field label="Potential Disruptions">
              {answers.possible_disturbances || "—"}
            </Field>
            <Divider />
            <Field label="On-the-day Contact">
              {contactName(answers.on_the_day_contact, contact_names)}
            </Field>
          </div>
        </section>

        {/* Cohort Demographics */}
        <section className="flex flex-col gap-5">
          <SectionHeader.Root>
            <SectionHeader.Group>
              <SectionHeader.Heading>Cohort Demographics</SectionHeader.Heading>
            </SectionHeader.Group>
          </SectionHeader.Root>
          <div className="flex flex-col gap-5">
            <Field label="Critical Incidents">
              {answers.critical_incident_occurred === true
                ? formatList(answers.critical_incidents)
                : answers.critical_incident_occurred === false
                  ? "None reported"
                  : "—"}
            </Field>
            <Divider />
            <Field label="Cultural Diversity">
              {formatList(answers.cald_backgrounds)}
            </Field>
          </div>
        </section>

        {/* Support Services */}
        <section className="flex flex-col gap-5 pb-8">
          <SectionHeader.Root>
            <SectionHeader.Group>
              <SectionHeader.Heading>Support Services</SectionHeader.Heading>
            </SectionHeader.Group>
          </SectionHeader.Root>
          <div className="flex flex-col gap-5">
            <Field label="Wellbeing Staff">
              {contactName(answers.wellbeing_staff, contact_names)}
            </Field>
            <Divider />
            <Field label="Student Access Methods">
              {formatList(answers.student_access_methods)}
            </Field>
            <Divider />
            <Field label="After-hours Contact">
              {contactName(answers.after_hours_contact, contact_names)}
            </Field>
          </div>
        </section>
      </div>
    </DashboardLayout>
  )
}
